// frontend/src/modules/expenses/components/ApproveRejectDialog.tsx
import * as React from 'react';
import { toast } from 'sonner';
import { CheckCircle2, XCircle } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../../components/ui/dialog';
import { expensesApi } from '../api';
import type { Expense } from '../types';

interface ApproveRejectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'approve' | 'reject';
  expense: Expense;
  onDone: (next: Expense) => void;
}

export function ApproveRejectDialog({ open, onOpenChange, mode, expense, onDone }: ApproveRejectDialogProps) {
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => { if (open) { setReason(''); setBusy(false); } }, [open]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (mode === 'reject' && reason.trim().length === 0) {
      toast.error('Please provide a reason');
      return;
    }
    setBusy(true);
    try {
      const next = mode === 'approve'
        ? await expensesApi.approve(expense.id, reason.trim() || undefined)
        : await expensesApi.reject(expense.id, reason.trim());
      toast.success(mode === 'approve' ? 'Expense approved' : 'Expense rejected');
      onDone(next);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(`${mode === 'approve' ? 'Approve' : 'Reject'} failed`, { description: err.message ?? String(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'approve'
              ? <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Approve expense</>
              : <><XCircle className="h-4 w-4 text-rose-600" /> Reject expense</>}
          </DialogTitle>
          <DialogDescription>
            {mode === 'approve'
              ? `Mark this expense as approved. ${expense.employee_name} will be notified.`
              : `Send this expense back to ${expense.employee_name} with a reason.`}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 pt-2">
          <div className="grid gap-1.5">
            <Label htmlFor="decision-note">
              {mode === 'reject'
                ? <>Reason<span className="text-red-500 dark:text-red-400 ml-0.5">*</span></>
                : <>Note <span className="text-ink/40 dark:text-white/40 text-[12px]">(optional)</span></>}
            </Label>
            <Textarea
              id="decision-note"
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={mode === 'reject' ? 'Why is this expense being rejected?' : 'Add an approval note…'}
              required={mode === 'reject'}
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={busy}
              variant={mode === 'reject' ? 'destructive' : 'default'}
            >
              {busy ? 'Working…' : mode === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
